import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, fmtPrice } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Flame, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";

const HERO = "https://images.unsplash.com/photo-1484154218962-a197022b5858?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const HERO2 = "https://images.unsplash.com/photo-1639413665566-2f75adf7b7ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [departments, setDepartments] = useState({});
  const [newItems, setNewItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/featured?limit=10`).then((r) => setFeatured(r.data.products)).catch(() => {});
    axios.get(`${API}/catalog/departments`).then((r) => setDepartments(r.data)).catch(() => {});
    axios.get(`${API}/products?in_stock=true&limit=8`).then((r) => setNewItems(r.data.products)).catch(() => {});
    axios.get(`${API}/brands`).then((r) => setBrands(r.data.brands)).catch(() => {});
  }, []);

  const spotlight = featured[0];

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
      {/* HERO BENTO */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:grid-rows-2 lg:h-[460px]">
        <div className="relative overflow-hidden rounded-2xl lg:col-span-2 lg:row-span-2">
          <img src={HERO} alt="Électroménager" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/50 to-transparent" />
          <div className="relative flex h-full flex-col justify-center p-8 md:p-12">
            <span className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white">
              <Flame className="h-4 w-4" /> Offres du moment
            </span>
            <h1 className="font-head text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">
              Équipez votre maison<br />à petits prix
            </h1>
            <p className="mt-4 max-w-md text-base text-slate-200">
              Électroménager, cuisine, déco & mobilier. Des milliers de produits livrés chez vous.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/catalogue?featured=1">
                <Button data-testid="hero-offers-btn" className="rounded-full bg-blue-600 px-6 py-6 text-base text-white hover:bg-blue-700">
                  Voir les offres <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/catalogue">
                <Button variant="outline" className="rounded-full border-white/40 bg-white/10 px-6 py-6 text-base text-white hover:bg-white/20">
                  Tout le catalogue
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {spotlight ? (
          <Link to={`/produit/${spotlight.sku}`} className="group relative flex min-h-[220px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="relative min-h-0 flex-1 bg-slate-50">
              {spotlight.discount > 0 && (
                <span className="absolute left-3 top-3 z-10 rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold text-white">-{spotlight.discount}%</span>
              )}
              {spotlight.image && (
                <img src={spotlight.image} alt={spotlight.name} className="jc-prod-img h-full w-full object-contain p-6" />
              )}
            </div>
            <div className="shrink-0 border-t border-slate-100 p-4">
              <p className="line-clamp-2 text-sm font-semibold text-slate-800">{spotlight.name}</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-head text-xl font-extrabold text-slate-900">{fmtPrice(spotlight.price)}</span>
                {spotlight.oldPrice && <span className="text-sm text-slate-400 line-through">{fmtPrice(spotlight.oldPrice)}</span>}
              </div>
            </div>
          </Link>
        ) : (
          <div className="relative overflow-hidden rounded-2xl">
            <img src={HERO2} alt="High-tech" className="h-full w-full object-cover" />
          </div>
        )}

        <div className="relative hidden overflow-hidden rounded-2xl bg-blue-600 p-6 text-white lg:flex lg:flex-col lg:justify-center">
          <Sparkles className="h-8 w-8" />
          <h3 className="mt-3 font-head text-2xl font-extrabold">Jusqu'à -30%</h3>
          <p className="mt-1 text-sm text-blue-100">Sur une sélection de produits en stock. Profitez-en vite !</p>
          <Link to="/catalogue?featured=1" className="mt-4 text-sm font-bold underline">Découvrir →</Link>
        </div>
      </section>

      {/* OFFRES DU MOMENT */}
      <section className="mt-14">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-head text-2xl font-extrabold text-slate-900 md:text-3xl">Offres du moment</h2>
            <p className="text-sm text-slate-500">Nos meilleures promos, pendant qu'il y en a encore.</p>
          </div>
          <Link to="/catalogue?featured=1" className="hidden text-sm font-semibold text-blue-700 hover:underline md:block">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {featured.slice(0, 10).map((p) => <ProductCard key={p.sku} product={p} />)}
        </div>
      </section>

      {/* MARQUES */}
      <section className="mt-14">
        <div className="mb-6">
          <h2 className="font-head text-2xl font-extrabold text-slate-900 md:text-3xl">Nos marques phares</h2>
          <p className="text-sm text-slate-500">Des marques de confiance, sélectionnées pour vous.</p>
        </div>
        <div className="jc-scroll flex gap-4 overflow-x-auto pb-2">
          {brands.map((b) => (
            <Link
              key={b.name}
              to={`/catalogue?brand=${encodeURIComponent(b.name)}`}
              data-testid={`brand-${b.name}`}
              className="group flex min-w-[180px] flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-7 text-center hover:border-blue-300 hover:shadow-md transition-all"
            >
              <span className="font-head text-xl font-extrabold text-slate-800 group-hover:text-blue-700">{b.name}</span>
              <span className="mt-1 text-xs text-slate-400">{b.count} produits</span>
            </Link>
          ))}
        </div>
      </section>

      {/* RAYONS */}
      <section className="mt-14">
        <h2 className="mb-6 font-head text-2xl font-extrabold text-slate-900 md:text-3xl">Nos rayons</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {Object.keys(departments).map((d) => (
            <Link
              key={d}
              to={`/catalogue?department=${encodeURIComponent(d)}`}
              data-testid={`home-dept-${d}`}
              className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div>
                <p className="font-head font-bold text-slate-900 group-hover:text-blue-700">{d}</p>
                <p className="mt-1 text-xs text-slate-400">{(departments[d] || []).length} catégories</p>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      {/* EN STOCK */}
      <section className="mt-14">
        <div className="mb-6 flex items-end justify-between">
          <h2 className="font-head text-2xl font-extrabold text-slate-900 md:text-3xl">Disponibles immédiatement</h2>
          <Link to="/catalogue?in_stock=1" className="hidden text-sm font-semibold text-blue-700 hover:underline md:block">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {newItems.slice(0, 8).map((p) => <ProductCard key={p.sku} product={p} />)}
        </div>
      </section>
    </div>
  );
}
